<template>
  <div name="clubview" class="inner">
    <innertopbar @close="closeWindow()">
      <template v-slot:breadcrumb>
        <router-link to="activities"> Clubs </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>
        <li
          v-if="!newAddition"
          @click="deletion"
          v-tooltip="{
            content: 'Delete club',
            placement: 'bottom-center',
            offset: 5,
            classes: ['tooltip-info'],
          }"
          title="Delete club"
          class="action-list-item inline-block bg-list-grey hover:opacity-100"
        >
          <i class="icon fas fa-trash-alt" title="Delete club"></i>
        </li>

        <li
          v-if="!newAddition"
          class="inline-block mx-4 text-2xl text-gray-400"
        >
          |
        </li>

        <li class="inline-block">
          <button
            @click="submitForm()"
            type="button"
            class="publish-button hover:opacity-75"
          >
            Publish Changes
          </button>
        </li>
      </template>
    </innertopbar>

    <transition name="fade">
      <div class="loading" v-show="isSaving">
        <span class="fa fa-spinner fa-spin"></span> Saving
      </div>
    </transition>

    <div class="w-full mx-auto grid grid grid-flow-col grid-cols-2 gap-10">
      <div class="mt-6 ml-8 p-6 bg-white rounded-lg mb-10">
        <form
          class="w-95p mx-auto"
          v-on:submit.prevent="submitActivity"
          id="activityForm"
          ref="form"
        >
          <savenotice
            :title="'Pupil'"
            ref="saveNoticeTop"
            class="right"
          ></savenotice>

          <div class="flex flex-wrap -mx-3 mb-8">
            <div class="w-full">
              <label class="form-label" for="grid-title">
                Club Title
              </label>
              <input
                v-model="name"
                class="
                  mb-2
                  focus:border-red-200 focus:bg-white focus:outline-none
                "
                id="grid-title"
                type="text"
                placeholder="Enter club title"
              />
              <p class="text-red-300 text-xs italic">
                Please fill out this field.
              </p>
            </div>
          </div>

          <div class="flex flex-wrap -mx-3 mb-6">
            <div class="w-1/2">
              <label class="form-label" for="grid-title"> Club Type </label>
              <dropdown
                :options="typeDropdown"
                :selected="selectedType"
                v-on:updateOption="typeOnSelect"
                class="bg-gray-100"
              ></dropdown>
            </div>
          </div>

          <div class="flex flex-wrap -mx-3 mb-8">
            <div class="w-full">
              <label class="form-label" for="grid-title">
                Club Address
              </label>

              <textarea
                v-model="address"
                cols="30"
                rows="2"
                class="
                  p-2
                  focus:border-red-200 focus:bg-white focus:outline-none
                "
              ></textarea>
            </div>
          </div>

          <div class="flex flex-wrap -mx-3 mb-8">
            <div class="w-full">
              <label class="form-label" for="grid-title">
                Club Details
              </label>

              <textarea
                v-model="details"
                cols="30"
                rows="4"
                class="
                  p-2
                  focus:border-red-200 focus:bg-white focus:outline-none
                "
              ></textarea>
            </div>
          </div>

          <div class="flex flex-wrap -mx-3 mb-8">
            <div class="w-full">
              <label class="form-label" for="grid-title">
                Club Results
              </label>

              <textarea
                v-model="results"
                cols="30"
                rows="6"
                class="
                  p-2
                  focus:border-red-200 focus:bg-white focus:outline-none
                "
              ></textarea>
            </div>
          </div>

          <savenotice
            :title="'Pupil'"
            ref="saveNoticeBottom"
            class="right"
          ></savenotice>
        </form>
      </div>

      <div class="p-6 pt-10 bg-white">
        <div class="block h-1/2 overflow-y-auto">
          <h3 class="mb-6 ml-1 text-lg font-semibold">
            {{ pupilsSelected.length }} pupils at the club

            <div class="float-right">

              <button
                @click="showPupilGrid = true"
                type="button"
                class="
                  action-button
                  icon
                  hover:opacity-75
                  fas 
                  fa-plus
                  p-2
                  py-1
                  text-sm
                  mr-4
                "
              ></button>
            </div>
          </h3>

          <div
            style="width: 100%; text-align: center"
            class="spin-holder mt-4"
            v-if="loading"
          >
            <i class="spinner fa fa-spinner fa-spin"></i>
            <p>Loading Pupils</p>
          </div>

          <table class="table-auto w-full max-h-full" v-if="hasPupils">
            <thead class="bg-gray-100">
              <tr>
                <th class="w-7/12 text-left pl-2 py-1">Name</th>
                <th class="w-3/12 text-center pl-2">Year Group</th>
                <th class="w-2/12 text-center"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="hover:bg-gray-300 cursor-pointer even:bg-gray-100"
                @click="selectPupil(pupil)"
                v-for="pupil in pupilsSelectedList"
                :key="pupil.identifier"
              >
                <td>{{ pupil.name }}</td>
                <td class="text-center">{{ pupilYearGroupDisplay(pupil) }}</td>
                <td class="text-center">
                  <i
                    class="fas fa-check text-base"
                    v-bind:class="selectedPupilStyle(pupil)"
                  ></i>
                </td>
              </tr>
            </tbody>
          </table>

          <p class="m-5 mx-12" v-else>
            We could not find any data for your given selection.
          </p>
        </div>
      </div>
    </div>

    <ModalSlide :show="showPupilGrid"  @closeModal="closePupilSelection">
      <template v-slot:header-icon>
        <i class="fas fa-check-circle"></i>
       </template>
      <template v-slot:header-title>
       Select pupils for club
       </template>
      <template v-slot:body>
        <PupilsSelect
              :selectedPupils="pupilsSelectedList"
              @pupil-selected="selectPupil"
              :allowMultiple="true"
              @close="closePupilSelection"
            ></PupilsSelect>
      </template>
    </ModalSlide>

  </div>
</template>

<script>
const innertopbar = () => import("@/components/InnerTopBar.vue");
const savenotice = () => import("@/components/SaveNotice.vue");
const dropdown = () => import("@/components/Dropdown.vue");
const doublePicker = () => import("@/components/helpers/DoublePicker.vue");
const PupilsSelect = () => import("@/components/PupilsListSelect.vue");
const ModalSlide = () => import("@/components/ModalSlide.vue");

import { mapGetters } from "vuex";
import { ClubService } from "@/services/club.service";

import {
  calendarYearList,
  eventTypes,
} from "@/assets/js/utils";

import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";

export default {
  name: "clubview",
  mixins: [yearGroupMixin],
  props: {
    newAddition: { type: Boolean, required: true },
    club: { type: Object, required: true },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    isDisabled() {
      return false;
    },
    typeDropdown() {
      var results = [{ id: 0, name: "Select a type" }].concat(this.types);
      if (this.newAddition) {
        this.selectedType = results[1];
      } else if (this.club.identifier === undefined) {
        this.selectedType = results[1];
      }
      return results;
    },
    hasPupils() {
      return this.pupilsSelected.length > 0;
    },
    title() {
      if (this.newAddition) {
        return "Create";
      } else {
        return this.name;
      }
    },
    pupilsSelectedList() {
      return this.pupilsSelected;
    },
  },
  data() {
    return {
      school: this.$store.getters.school,
      eventInEdit: true,
      name: "",
      details: "",
      address: "",
      url: "",
      results: "",
      selectedType: {},
      pupils: [],
      pupilsSelected: [],
      pupilsRemoved: [],
      loading: false,
      errored: false,
      types: eventTypes(),

      showPupilGrid: false,

      isSaving: false,
    };
  },
  created() {
    if (this.newAddition == false) {
      if (this.club == undefined) {
        this.closeWindow();
        return;
      } else {
         this.name = this.club.name == undefined ? "" : this.club.name;
         this.details = this.club.description;
         this.address = this.club.address
         this.url = this.club.url
         this.results = this.club.results

        var foundType = this.types.find((tt) => {
          return tt.value == this.club.program_type;
        });
        this.selectedType = foundType != undefined ? foundType : this.types[0];

        this.fetchPupils();
      }
    }
  },
  mounted() {
  },
  methods: {
    fetchPupils() {
      this.loading = true;

      ClubService.getPupils(this.club)
        .then((response) => (this.pupils = response.data.data))
        .catch((err) => {
          console.log("activities pupils " + err);
          this.errored = true;
        })
        .finally(() => {
          this.pupilsSelected = this.pupils.slice();
          this.loading = false;
        });
    },
    pupilYearGroupDisplay(pupil) {
      let found = this.getYearGroupFromId(pupil.year.id);
      return found != undefined ? found.name : "Unknown";
    },
    submitForm() {
      this.isSaving = true;

      if (this.$refs.form.requestSubmit === undefined) {
        this.processActivity();
      } else {
        this.$refs.form.requestSubmit();
      }
    },
    submitActivity() {
      if (
        this.name == "" ||
        this.selectedType.id === 0
      ) {
        this.alertDisplay();
        this.isSaving = false;
      } else {
        this.processActivity();
      }
    },
    alertDisplay() {
      this.$swal({
        title: "Please complete all fields",
        text: "You must enter a title and set a class to complete the form.",
        type: "warning",
        showCancelButton: false,
        showCloseButton: true,
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result === true) {
          //this.processPupil();
        }
      });
    },
    processActivity() {
      this.club.name = this.name;
      this.club.program_type = this.selectedType.value;
      this.club.description = this.details;
      this.club.address = this.address
      this.club.url = this.url
      this.club.results = this.results

      console.log(JSON.stringify(this.club))

      if (this.club.identifier === undefined) {
        let calendarYear = calendarYearList().find((y) => y.current === true);
        this.club.calendar_schoolyear = calendarYear.value;
        this.club.longitude = 0
        this.club.longitude = 0 

        ClubService.add(this.club)
          .then((response) => {
            this.club.identifier = response.data.identifier;
            this.completion();
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        ClubService.update(this.club)
          .then((response) => this.completion(response.data))
          .catch((err) => {
            console.log(err);
          });
      }
    },
    completion(msg) {
      var vm = this;
      vm.pupilsRemoved.forEach((p) => {
        ClubService.deletePupil(vm.club, p).catch((err) => {
          console.log(err);
        });
      });

      vm.pupilsSelected.forEach((p) => {
        let payload = {
          club_identifier: vm.club.identifier,
          register_identifier: p.identifier,
        };
        ClubService.addPupil(payload).catch((err) => {
          console.log(err);
        });
      });

      this.isSaving = false;
      this.$router.push({ path: "/toolbox/clubs" , params: { msg: msg } });
      window.scrollTo(0, 0);
    },
    failure(error) {
      this.$refs.saveNoticeTop.failed();
      this.$refs.saveNoticeBottom.failed();
      console.log("club update error " + error);
    },
    typeOnSelect(value) {
      this.selectedType = value;
    },
    selectedPupilStyle(pupil) {
      return this.isSelected(pupil) ? "text-green-300" : "text-gray-200";
    },
    isSelected(value) {
      let found = this.pupilsSelected.find(
        (p) => p.identifier === value.identifier
      );
      return found;
    },
    selectPupil(pupil) {
      if (this.pupilsSelected.some((p) => p.identifier === pupil.identifier)) {
        this.pupilsSelected.splice(
          this.pupilsSelected.findIndex(
            (item) => item.identifier === pupil.identifier
          ),
          1
        );
        var toRemove = this.pupils.find(
          (p) => p.identifier === pupil.identifier
        );
        if (toRemove != undefined) {
          this.pupilsRemoved.push(pupil);
        }
      } else {
        this.pupilsSelected.push(pupil);
        var toRemove = this.pupilsRemoved.find(
          (p) => p.identifier === pupil.identifier
        );
        if (toRemove != undefined) {
          this.pupilsRemoved.splice(
            this.pupilsRemoved.findIndex(
              (item) => item.identifier === pupil.identifier,
              1
            )
          );
        }
      }
    },
    deletion() {
      this.$swal({
        title: "Are you sure you want to delete?",
        text: "You will not be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
      }).then((result) => {
        if (result) {
          this.processDelete();
        }
      });
    },
    processDelete() {
      ClubService.delete(this.club)
        .then((response) => this.completion(response.data))
        .catch((err) => {
          console.log(err);
        });
    },
    closeWindow() {
      this.$router.push({ path: "/toolbox/clubs" });
      window.scrollTo(0, 0);
    },
    closePupilSelection() {
      this.showPupilGrid = false;
    },

  },
  components: {
    dropdown,
    savenotice,
    doublePicker,
    innertopbar,
    PupilsSelect,
    ModalSlide
  },
};
</script>


<style scoped>

</style>
